import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "moment/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { api } from "@/lib/api";
import { toast } from "sonner";
import QuickBookDialog from "@/components/QuickBookDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Ban } from "lucide-react";

moment.locale("es");
const localizer = momentLocalizer(moment);

const MESSAGES = {
  allDay: "Todo el día", previous: "Anterior", next: "Siguiente", today: "Hoy",
  month: "Mes", week: "Semana", day: "Día", agenda: "Agenda",
  date: "Fecha", time: "Hora", event: "Cita", noEventsInRange: "Sin citas en este rango.",
  showMore: (n) => `+${n} más`,
};

// deterministic pastel color per staff id
const STAFF_COLORS = ["#1E3A8A", "#059669", "#D97706", "#7C3AED", "#DB2777", "#0891B2", "#BE123C", "#4D7C0F"];
function colorFor(staffId) {
  if (!staffId) return "#64748B";
  let h = 0; for (let i = 0; i < staffId.length; i++) h = (h * 31 + staffId.charCodeAt(i)) | 0;
  return STAFF_COLORS[Math.abs(h) % STAFF_COLORS.length];
}

function toLocalDate(dateStr, timeStr, durationMin) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = (timeStr || "00:00").split(":").map(Number);
  const start = new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
  const end = new Date(start.getTime() + (durationMin || 30) * 60000);
  return { start, end };
}

function slotToTime(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function dateToISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function AdminCalendar() {
  const [appts, setAppts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState("all");
  const [dialog, setDialog] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [a, s, sv, b] = await Promise.all([
        api.get("/appointments"),
        api.get("/staff"),
        api.get("/services"),
        api.get("/admin/staff-blocks").catch(() => ({ data: [] })),
      ]);
      setAppts(a.data || []);
      setStaff(s.data || []);
      setServices(sv.data || []);
      setBlocks(b.data || []);
    } catch (e) {
      toast.error("Error al cargar el calendario");
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredAppts = useMemo(() => {
    if (selectedStaff === "all") return appts;
    if (selectedStaff === "unassigned") return appts.filter((a) => !a.staff_id);
    return appts.filter((a) => a.staff_id === selectedStaff);
  }, [appts, selectedStaff]);

  const filteredBlocks = useMemo(() => {
    if (selectedStaff === "all" || selectedStaff === "unassigned") return blocks;
    return blocks.filter((b) => b.staff_id === selectedStaff);
  }, [blocks, selectedStaff]);

  const staffNameById = useMemo(() => {
    const m = {};
    staff.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [staff]);

  const events = useMemo(() => {
    const apptEvents = filteredAppts
      .filter((a) => a.status !== "cancelled" && a.date && a.time)
      .map((a) => {
        const { start, end } = toLocalDate(a.date, a.time, a.service_duration_min || 30);
        return {
          id: a.id,
          title: `${a.client_name} · ${a.service_name}${a.staff_name ? " · " + a.staff_name : ""}`,
          start,
          end,
          resource: { kind: "appointment", ...a },
        };
      });

    const blockEvents = (filteredBlocks || []).map((b) => {
      const [sy, sm, sd] = b.start_date.split("-").map(Number);
      const [sh, smin] = (b.start_time || "00:00").split(":").map(Number);
      const [ey, em, ed] = b.end_date.split("-").map(Number);
      const [eh, emin] = (b.end_time || "23:59").split(":").map(Number);
      const start = new Date(sy, sm - 1, sd, sh, smin, 0, 0);
      const end = new Date(ey, em - 1, ed, eh, emin, 0, 0);
      const staffName = staffNameById[b.staff_id] || "";
      const reason = b.reason || "Bloqueo";
      return {
        id: `block-${b.id}`,
        title: `🚫 ${reason}${staffName ? " · " + staffName : ""}`,
        start,
        end,
        resource: { kind: "block", ...b },
      };
    });

    return [...apptEvents, ...blockEvents];
  }, [filteredAppts, filteredBlocks, staffNameById]);

  // Determine business hours for week/day views based on staff schedules.
  const [minTime, maxTime] = useMemo(() => {
    let minH = 8, maxH = 20;
    const consider = selectedStaff === "all" ? staff : staff.filter((s) => s.id === selectedStaff);
    (consider || []).forEach((st) => {
      DAYS.forEach((k) => {
        const c = (st.schedule || {})[k] || {};
        if (c.enabled && c.start && c.end) {
          const sh = parseInt(c.start.split(":")[0], 10);
          const eh = parseInt(c.end.split(":")[0], 10);
          if (!Number.isNaN(sh)) minH = Math.min(minH, sh);
          if (!Number.isNaN(eh)) maxH = Math.max(maxH, eh);
        }
      });
    });
    const dref = new Date();
    dref.setHours(minH, 0, 0, 0);
    const dmax = new Date();
    dmax.setHours(Math.min(23, Math.max(minH + 1, maxH)), 0, 0, 0);
    return [dref, dmax];
  }, [staff, selectedStaff]);

  // Style event by staff color (or striped grey for blocks)
  const eventPropGetter = useCallback((event) => {
    if (event.resource?.kind === "block") {
      return {
        style: {
          backgroundColor: "#475569",
          backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 6px, transparent 6px 12px)",
          borderColor: "#334155",
          color: "#fff",
          borderRadius: 6,
          border: "0",
          fontSize: 12,
          padding: "2px 6px",
          fontStyle: "italic",
        },
      };
    }
    const c = colorFor(event.resource?.staff_id);
    return {
      style: {
        backgroundColor: c,
        borderColor: c,
        color: "#fff",
        borderRadius: 6,
        border: "0",
        fontSize: 12,
        padding: "2px 6px",
      },
    };
  }, []);

  // Grey-out slots outside working hours (week/day view). We approximate: if the
  // selectedStaff has no working schedule for that weekday/time, mark as unavailable.
  const slotPropGetter = useCallback((slotDate) => {
    if (view === Views.MONTH) return {};
    const key = DAYS[(slotDate.getDay() + 6) % 7];
    const minutes = slotDate.getHours() * 60 + slotDate.getMinutes();
    const check = (st) => {
      const c = (st.schedule || {})[key];
      if (!c || !c.enabled) return false;
      const [sh, sm] = (c.start || "00:00").split(":").map(Number);
      const [eh, em] = (c.end || "00:00").split(":").map(Number);
      const startMin = sh * 60 + (sm || 0);
      const endMin = eh * 60 + (em || 0);
      if (!(minutes >= startMin && minutes < endMin)) return false;
      const ls = st.lunch_start, le = st.lunch_end;
      if (ls && le) {
        const [lsh, lsm] = ls.split(":").map(Number);
        const [leh, lem] = le.split(":").map(Number);
        const lStart = lsh * 60 + (lsm || 0);
        const lEnd = leh * 60 + (lem || 0);
        if (minutes >= lStart && minutes < lEnd) return false;
      }
      return true;
    };
    const consider = selectedStaff === "all" || selectedStaff === "unassigned"
      ? (staff || [])
      : staff.filter((s) => s.id === selectedStaff);
    const anyOpen = consider.some(check);
    if (!anyOpen) {
      return { style: { backgroundColor: "#F1F5F9", borderTop: "1px dashed #E2E8F0" } };
    }
    return {};
  }, [staff, selectedStaff, view]);

  const handleSelectSlot = useCallback((slotInfo) => {
    const start = slotInfo.start;
    setDialog({
      date: dateToISO(start),
      time: slotToTime(start),
      staff_id: selectedStaff !== "all" && selectedStaff !== "unassigned" ? selectedStaff : "",
    });
  }, [selectedStaff]);

  const handleSelectEvent = useCallback(async (event) => {
    const r = event.resource;
    if (r.kind === "block") {
      const ok = window.confirm(`¿Eliminar este bloqueo?\n\n${r.reason || "Sin motivo"}\n${r.start_date} ${r.start_time} → ${r.end_date} ${r.end_time}`);
      if (!ok) return;
      try {
        await api.delete(`/admin/staff-blocks/${r.id}`);
        toast.success("Bloqueo eliminado");
        loadAll();
      } catch {
        toast.error("No se pudo eliminar el bloqueo");
      }
      return;
    }
    toast.message(`${r.client_name} · ${r.service_name}`, {
      description: `${r.staff_name || "Sin asignar"} — ${r.date} ${r.time} · ${r.status}`,
    });
  }, [loadAll]);

  const dayPropGetter = useCallback((d) => {
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return { style: { backgroundColor: "#EFF6FF" } };
    return {};
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-calendar-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl">Calendario</h1>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const now = new Date();
              setDialog({
                date: dateToISO(now),
                time: `${String(now.getHours()).padStart(2, "0")}:${now.getMinutes() < 30 ? "00" : "30"}`,
                staff_id: selectedStaff !== "all" && selectedStaff !== "unassigned" ? selectedStaff : "",
                openBlockTab: true,
              });
            }}
            data-testid="calendar-add-block-btn"
          >
            <Ban className="h-4 w-4 mr-2" /> Bloquear tiempo
          </Button>
          <Select value={selectedStaff} onValueChange={setSelectedStaff}>
            <SelectTrigger className="w-56" data-testid="calendar-staff-filter"><SelectValue placeholder="Filtrar por profesional" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los profesionales</SelectItem>
              <SelectItem value="unassigned">Sin asignar</SelectItem>
              {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 sm:p-6">
          <div style={{ height: 720 }} data-testid="calendar-container">
            <Calendar
              localizer={localizer}
              culture="es"
              messages={MESSAGES}
              events={events}
              startAccessor="start"
              endAccessor="end"
              views={[Views.MONTH, Views.WEEK, Views.DAY]}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              selectable
              step={30}
              timeslots={2}
              min={minTime}
              max={maxTime}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventPropGetter}
              slotPropGetter={slotPropGetter}
              dayPropGetter={dayPropGetter}
              popup
              tooltipAccessor={(e) => e.resource?.kind === "block"
                ? `Bloqueo: ${e.resource.reason || "sin motivo"}\n${staffNameById[e.resource.staff_id] || ""}\n${e.resource.start_date} ${e.resource.start_time} → ${e.resource.end_date} ${e.resource.end_time}`
                : `${e.resource.client_name}\n${e.resource.service_name}\n${e.resource.staff_name || "Sin asignar"}\n${e.resource.date} ${e.resource.time}`}
            />
          </div>
        </CardContent>
      </Card>

      {dialog && (
        <QuickBookDialog
          initial={dialog}
          staffList={staff}
          services={services}
          onClose={() => setDialog(null)}
          onCreated={(kind) => { setDialog(null); loadAll(); toast.success(kind === "block" ? "Bloqueo creado" : "Cita creada"); }}
        />
      )}
    </div>
  );
}
